import React, { useState, useEffect, useCallback } from "react";
import { toast } from 'react-toastify';
import { useNavigate } from "react-router-dom";
import LeftNav from "../components/common/LeftNav";
import TopNav from "../components/common/TopNav";
import "../assets/styles/Webadminbillingsummary.css";
import arrowleftt from "../assets/img/arrowleftt.svg";
import downloadIcon from "../assets/img/export.svg";
import filterIcon from "../assets/img/filter1.svg";
import invoicewebadmin from "../assets/img/invoicewedadmin.svg";
import attachementwebadmin from "../assets/img/attachmentwebadmin.svg";
import close from "../assets/img/close-square.svg";
import adminCrmService from "../../services/adminCrmService";
import * as XLSX from "xlsx";
import rebaxLogo from "../assets/img/rebax-logo-horizontal.png";
import { IoCheckmarkCircle } from "react-icons/io5";

const COMPANY_ADDRESS = 'Flat No 101, Plot No B-125, BK Enclave, Reyes Abode, Miyapur, Line No-5, Serilingampally, Hyderabad, Medchal Malkajgiri, Telangana - 500049.';
const COMPANY_GST = '36AAPCR4264B1ZB';
const COMPANY_PAN = 'AAPCR4264B';

const PERIODS = [
  { key: "this_month", label: "This Month" },
  { key: "previous_month", label: "Previous Month" },
  { key: "this_year", label: "This Year" },
];

export default function Webadminbillingsummary() {
  const navigate = useNavigate();

  const [selectedPeriod, setSelectedPeriod] = useState("this_month");
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("invoices");
  const [searchQuery, setSearchQuery] = useState("");

  // Invoice detail modal
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  // Post-download popup (matches app's pdfModal)
  const [pdfModal, setPdfModal] = useState({ visible: false, fileName: "", html: "", attachmentPdfUrl: null });

  // Add Expense Modal
  const [showAddExpenseModal, setShowAddExpenseModal] = useState(false);
  const [expenseForm, setExpenseForm] = useState({ name: "", amount: "", date: "", attachment: null });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [addingExpense, setAddingExpense] = useState(false);

  // Excel export
  const [exportStartDate, setExportStartDate] = useState("");
  const [exportEndDate, setExportEndDate] = useState("");
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  // Filter by date (matches app filterStartDate/filterEndDate)
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterStartInput, setFilterStartInput] = useState("");
  const [filterEndInput, setFilterEndInput] = useState("");

  // ── Helpers ──────────────────────────────────────────────────────────────
  const fmtNum = (val) => {
    const num = parseFloat(val) || 0;
    return num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Matches mobile app: green ↑ / red ↓ "% vs last month", hidden when 0 or missing
  const renderStatChange = (change) => {
    if (typeof change !== "number" || isNaN(change) || change === 0) return null;
    return (
      <div style={{ fontSize: 12, fontWeight: 500, marginTop: 6, color: change >= 0 ? "#059669" : "#DC2626" }}>
        {change >= 0 ? "↑" : "↓"}{Math.abs(change).toFixed(1)}% vs last month
      </div>
    );
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";

  const toLocalDateStr = (d) => {
    if (!d) return null;
    const date = new Date(d);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };

  // ── Data fetch (matches app exactly: period OR dates, never both) ─────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params =
        filterStartDate && filterEndDate
          ? { start_date: toLocalDateStr(filterStartDate), end_date: toLocalDateStr(filterEndDate) }
          : { period: selectedPeriod };

      const [summaryRes, invoicesRes, expensesRes] = await Promise.all([
        adminCrmService.getBillingSummary(params.period || null, params.start_date, params.end_date),
        adminCrmService.getInvoices({ ...params, limit: 50 }),
        adminCrmService.getExpenses(params),
      ]);
      if (summaryRes.success) setStats(summaryRes.data);
      if (invoicesRes.success) setInvoices(invoicesRes.data || []);
      if (expensesRes.success) setExpenses(expensesRes.data || []);
    } catch (err) {
      console.error("Fetch billing data error:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod, filterStartDate, filterEndDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Invoice / expense filtering (matches app exactly) ────────────────────
  const userInvoices = invoices.filter(
    (i) => !i.is_expense && !["admin_expense", "admin_assignment", "admin_extension"].includes(i.payment_method)
  );

  const q = searchQuery.trim().toLowerCase();
  const filteredInvoices = q
    ? userInvoices.filter(
        (i) =>
          (i.order_id && i.order_id.toLowerCase().includes(q)) ||
          (i.invoice_number && i.invoice_number.toLowerCase().includes(q)) ||
          (i.transaction_id && i.transaction_id.toLowerCase().includes(q)) ||
          (i.user_name && i.user_name.toLowerCase().includes(q)) ||
          (i.user_email && i.user_email.toLowerCase().includes(q)) ||
          (i.plan_name && i.plan_name.toLowerCase().includes(q))
      )
    : userInvoices;

  const filteredExpenses = q
    ? expenses.filter(
        (i) =>
          (i.order_id && i.order_id.toLowerCase().includes(q)) ||
          (i.invoice_number && i.invoice_number.toLowerCase().includes(q)) ||
          (i.expense_name && i.expense_name.toLowerCase().includes(q)) ||
          (i.description && i.description.toLowerCase().includes(q))
      )
    : expenses;

  // Print invoice HTML via a hidden iframe (browser "Save as PDF")
  const printInvoiceHtml = (htmlContent) => new Promise((resolve) => {
    const printIframe = document.createElement("iframe");
    printIframe.style.position = "fixed";
    printIframe.style.top = "-9999px";
    printIframe.style.left = "-9999px";
    printIframe.style.width = "1px";
    printIframe.style.height = "1px";
    printIframe.style.border = "none";
    document.body.appendChild(printIframe);
    printIframe.contentDocument.write(htmlContent);
    printIframe.contentDocument.close();
    // Wait for the logo and attached bill image to load (or fail) so they aren't blank in the PDF
    const imagesReady = Promise.all(
      Array.from(printIframe.contentDocument.images).map((img) => (img.complete
        ? Promise.resolve()
        : new Promise((done) => { img.onload = done; img.onerror = done; })))
    );
    imagesReady.then(() => setTimeout(() => {
      printIframe.contentWindow.focus();
      printIframe.contentWindow.print();
      printIframe.contentWindow.onafterprint = () => {
        if (document.body.contains(printIframe)) document.body.removeChild(printIframe);
      };
      setTimeout(() => {
        if (document.body.contains(printIframe)) {
          document.body.removeChild(printIframe);
        }
      }, 3000);
      resolve();
    }, 500));
  });

  // ── Invoice PDF (exact same HTML as app's SubscriptionInvoiceModal) ─────────
  const handleDownloadInvoicePDF = async (invoice, e) => {
    e.stopPropagation();
    try {
      // Parse metadata if string (matches app)
      let inv = { ...invoice };
      if (inv.metadata && typeof inv.metadata === "string") {
        try { inv.metadata = JSON.parse(inv.metadata); } catch { inv.metadata = null; }
      }

      const isExpenseInvoice = inv.payment_method === "admin_expense" || inv.metadata?.type === "admin_expense";
      const paymentStatus = inv.payment_status === "paid" ? "Paid" : inv.payment_status === "free" ? "Free" : inv.payment_status === "expense" ? "Expense" : "Pending";

      const rolePrefix = inv.role === "agent" ? "AG" : inv.role === "agency" ? "AY" : "BU";
      const fallbackOrderId = `${rolePrefix}${(inv.id || "").toString().replace(/-/g, "").slice(0, 8).toUpperCase()}`;
      const invoiceNumber = inv.invoice_number || `INV-${fallbackOrderId}`;
      const orderId = inv.order_id || fallbackOrderId;
      const invoiceDate = inv.paid_at || inv.issued_at || inv.created_at
        ? new Date(inv.paid_at || inv.issued_at || inv.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        : "N/A";
      const planName = inv.plan_name || "Subscription Plan";
      const duration = inv.duration_days && inv.duration_days > 0 ? `${inv.duration_days} days` : "N/A";
      const startDate = inv.start_date ? new Date(inv.start_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "N/A";
      const endDate = inv.end_date ? new Date(inv.end_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "N/A";
      const fmtA = (val) => { if (!val && val !== 0) return "0"; return Number(val).toLocaleString("en-IN"); };
      const subtotal = fmtA(inv.amount);
      const tax = fmtA(inv.tax_amount);
      const total = fmtA(inv.total_amount || inv.amount);
      const currency = inv.currency || "INR";
      const sym = currency === "INR" ? "₹" : currency;
      const txnId = inv.transaction_id || "N/A";

      // Attached bill for expense invoices (matches app): image → extra page in the invoice,
      // PDF → note page in the invoice + the bill downloaded as a separate file
      const attachmentPath = isExpenseInvoice ? (inv.metadata?.attachment_url || inv.attachment_url) : null;
      const attachmentName = inv.metadata?.attachment_name || inv.attachment_name || "Bill_Attachment.pdf";
      const attachmentUrl = attachmentPath
        ? (attachmentPath.startsWith("http") ? attachmentPath : `${process.env.REACT_APP_API_BASE_URL || "https://sandbox.rebax.in"}${attachmentPath}`)
        : null;
      const attachmentExt = attachmentPath ? attachmentPath.split("?")[0].split(".").pop().toLowerCase() : "";
      const attachmentType = ["jpg", "jpeg", "png", "webp"].includes(attachmentExt) ? "image" : attachmentExt === "pdf" ? "pdf" : null;
      const attachmentTitle = `<div style="font-size:18px;font-weight:700;color:#6C63FF;margin-bottom:16px;border-bottom:2px solid #6C63FF;padding-bottom:10px;">Attached Bill</div>`;
      let attachmentPageHtml = "";
      if (attachmentUrl && attachmentType === "image") {
        attachmentPageHtml = `
  <div style="page-break-before:always;padding:20px;text-align:center;">
    ${attachmentTitle}
    <img src="${attachmentUrl}" style="max-width:100%;max-height:90vh;object-fit:contain;border:1px solid #EEE;border-radius:8px;" />
  </div>`;
      } else if (attachmentUrl && attachmentType === "pdf") {
        attachmentPageHtml = `
  <div style="page-break-before:always;padding:40px;text-align:center;">
    ${attachmentTitle}
    <div style="background:#F8F7FF;border-radius:12px;padding:30px;margin-top:20px;">
      <p style="font-size:14px;color:#666;">The original bill is a PDF document attached separately.</p>
      <p style="font-size:13px;color:#888;margin-top:8px;">File: ${attachmentName}</p>
    </div>
  </div>`;
      }
      const paymentMethod = inv.payment_method ? inv.payment_method.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "N/A";
      const userInfo = { name: inv.user_name, email: inv.user_email };

      // Plan Breakdown (metadata.premium) — matches app exactly
      let planBreakdownHtml = "";
      if (inv.metadata?.premium) {
        const proRateNote = inv.metadata.purchase_type === "modify" && inv.metadata.addons?.[0]?.remaining_days
          ? `<div style="background:#F0EDFF;border-radius:6px;padding:8px 12px;margin-bottom:10px;font-size:12px;color:#6C63FF;">&#8505; Pro-rated for ${inv.metadata.addons[0].remaining_days} days remaining until plan validity${inv.metadata.addons[0].plan_validity_end ? ` (${new Date(inv.metadata.addons[0].plan_validity_end).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })})` : ""}</div>`
          : "";
        const addonsRows = (inv.metadata.addons || []).map((addon) =>
          `<tr><td>${addon.plan_name}${addon.remaining_days ? `<br><span style="font-size:11px;color:#888;">Full: ${sym}${Number(addon.full_price || 0).toLocaleString("en-IN")} · Charged ${addon.remaining_days}/${addon.full_plan_days || "—"} days</span>` : ""}</td><td style="text-align:center;">${addon.quantity}</td><td style="text-align:right;">${sym} ${fmtA(addon.total)}</td></tr>`
        ).join("");
        const premiumRow = inv.metadata.premium.amount > 0
          ? `<tr><td>${inv.metadata.premium.plan_name || planName}</td><td style="text-align:center;">1</td><td style="text-align:right;">${sym} ${fmtA(inv.metadata.premium.amount)}</td></tr>`
          : "";
        planBreakdownHtml = `
          <div class="section">
            <div class="section-title">Plan Breakdown</div>
            ${proRateNote}
            <table><thead><tr><th>Item</th><th style="text-align:center;">Qty</th><th style="text-align:right;">Amount</th></tr></thead>
            <tbody>${premiumRow}${addonsRows}</tbody></table>
          </div>`;
      }

      // Totals section — Free vs Paid (matches app exactly)
      let totalsHtml = "";
      if (paymentStatus === "Free") {
        const planPrice = Number(subtotal !== "0" ? inv.amount : (inv.total_amount || 0));
        const gstAmt = Number(tax !== "0" ? inv.tax_amount : Math.round(planPrice * 0.18));
        const grandTotal = fmtA(planPrice + gstAmt);
        const discountAmt = fmtA(planPrice + gstAmt);
        totalsHtml = `
          <div class="total-row"><span>Plan Price</span><span>${sym} ${subtotal !== "0" ? subtotal : total}</span></div>
          <div class="total-row"><span>GST (18%)</span><span>${sym} ${tax !== "0" ? tax : fmtA(Math.round(Number(inv.amount || inv.total_amount || 0) * 0.18))}</span></div>
          <div class="total-row" style="font-weight:600;border-top:1px solid #E5E2FF;padding-top:8px;margin-top:4px;"><span>Total</span><span>${sym} ${grandTotal}</span></div>
          <div class="total-row" style="color:#3CB46E;font-weight:600;"><span>Discount (Complimentary - 100%)</span><span>- ${sym} ${discountAmt}</span></div>
          <div class="total-row grand"><span>Net Payable</span><span>${sym} 0</span></div>
          <div style="background:#EDE8FD;border-radius:6px;padding:8px 12px;margin-top:10px;font-size:12px;color:#816CF6;text-align:center;">This subscription was provided as complimentary. Full value shown for accounting purposes.</div>`;
      } else {
        totalsHtml = `
          <div class="total-row"><span>Subtotal</span><span>${sym} ${subtotal}</span></div>
          <div class="total-row"><span>Tax (GST)</span><span>${sym} ${tax}</span></div>
          <div class="total-row grand"><span>Total Amount</span><span>${sym} ${total}</span></div>`;
      }

      // Same file name as app; the <title> becomes the default name in the browser's "Save as PDF"
      const fileName = isExpenseInvoice
        ? `Rebax_Expense_Invoice_${invoiceNumber.replace(/[^a-zA-Z0-9]/g, "_")}`
        : `Rebax_Subscription_Invoice_${invoiceNumber.replace(/[^a-zA-Z0-9]/g, "_")}`;

      const htmlContent = `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>${fileName}</title><style>
  @page { margin: 30px; size: A4; }
  * { box-sizing: border-box; margin: 0; padding: 0; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
  body { font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; color: #333; line-height: 1.5; padding: 20px; }
  .invoice-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; border-bottom: 3px solid #6C63FF; padding-bottom: 20px; }
  .company-logo { width: 160px; height: 41px; }
  .company-meta { font-size: 11px; color: #666; margin-top: 6px; max-width: 280px; line-height: 1.4; }
  .company-meta strong { color: #333; }
  .invoice-title { font-size: 24px; font-weight: 700; color: #333; text-align: right; }
  .invoice-number { font-size: 14px; color: #666; text-align: right; margin-top: 4px; }
  .status-badge { display: inline-block; padding: 4px 16px; border-radius: 12px; font-size: 13px; font-weight: 600; margin-top: 8px; }
  .status-paid { background: #E6F4EC; color: #3CB46E; }
  .status-free { background: #EDE8FD; color: #816CF6; }
  .status-expense { background: #FEE2E2; color: #DC2626; }
  .status-pending { background: #FEF3C7; color: #F59E0B; }
  .section { margin-bottom: 24px; }
  .section-title { font-size: 13px; font-weight: 600; color: #6C63FF; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px; border-bottom: 1px solid #EEE; padding-bottom: 6px; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .info-item { margin-bottom: 6px; }
  .info-label { font-size: 12px; color: #888; }
  .info-value { font-size: 14px; color: #333; font-weight: 500; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th { background: #F8F7FF; color: #6C63FF; font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; padding: 10px 12px; text-align: left; border-bottom: 2px solid #E5E2FF; }
  td { padding: 10px 12px; font-size: 14px; color: #333; border-bottom: 1px solid #F0F0F0; }
  .amount-row td { font-weight: 600; }
  .total-section { margin-top: 16px; border-top: 2px solid #E5E2FF; padding-top: 16px; }
  .total-row { display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px; }
  .total-row.grand { font-size: 18px; font-weight: 700; color: #6C63FF; background: #F0EDFF; border-top: 2px solid #6C63FF; padding: 12px; margin-top: 8px; border-radius: 6px; }
  .payment-info { background: #F8F7FF; border-radius: 8px; padding: 16px; margin-top: 16px; }
  .payment-row { display: flex; justify-content: space-between; padding: 4px 0; font-size: 13px; }
  .payment-label { color: #888; }
  .payment-value { color: #333; font-weight: 500; }
  .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #AAA; border-top: 1px solid #EEE; padding-top: 16px; }
</style></head><body>

  <div class="invoice-header">
    <div>
      <img class="company-logo" src="${new URL(rebaxLogo, window.location.origin).href}" />
      <div class="company-meta">Rebax Solutions Private Limited</div>
      <div class="company-meta">${COMPANY_ADDRESS}</div>
      <div class="company-meta"><strong>GST No:</strong> ${COMPANY_GST}</div>
      <div class="company-meta"><strong>PAN No:</strong> ${COMPANY_PAN}</div>
    </div>
    <div>
      <div class="invoice-title">${isExpenseInvoice ? "EXPENSE INVOICE" : "SUBSCRIPTION INVOICE"}</div>
      <div class="invoice-number">${invoiceNumber}</div>
      ${orderId ? `<div class="invoice-number" style="margin-top:2px;">Order ID: ${orderId}</div>` : ""}
      <div style="text-align:right;margin-top:8px;">
        <span class="status-badge status-${paymentStatus.toLowerCase()}">${paymentStatus}</span>
      </div>
    </div>
  </div>

  <div class="section">
    <div class="section-title">Invoice Details</div>
    <div class="info-grid">
      <div class="info-item"><div class="info-label">Invoice Date</div><div class="info-value">${invoiceDate}</div></div>
      <div class="info-item"><div class="info-label">Invoice Number</div><div class="info-value">${invoiceNumber}</div></div>
      ${orderId ? `<div class="info-item"><div class="info-label">Order ID</div><div class="info-value">${orderId}</div></div>` : ""}
    </div>
  </div>

  <div class="section">
    <div class="section-title">Bill To</div>
    <div class="info-grid">
      ${userInfo?.name ? `<div class="info-item"><div class="info-label">Name</div><div class="info-value">${userInfo.name}</div></div>` : ""}
      ${userInfo?.email ? `<div class="info-item"><div class="info-label">Email</div><div class="info-value">${userInfo.email}</div></div>` : ""}
    </div>
  </div>

  <div class="section">
    <div class="section-title">Plan Details</div>
    <table>
      <thead><tr><th>Description</th><th>SAC</th><th>Period</th><th>Start Date</th><th>End Date</th><th style="text-align:right;">Amount</th></tr></thead>
      <tbody>
        <tr class="amount-row">
          <td>${planName}</td>
          <td>998391</td>
          <td>${duration}</td>
          <td>${startDate}</td>
          <td>${endDate}</td>
          <td style="text-align:right;">${sym} ${subtotal}</td>
        </tr>
      </tbody>
    </table>
  </div>

  ${planBreakdownHtml}

  <div class="total-section">
    ${totalsHtml}
  </div>

  <div class="payment-info">
    <div class="payment-row"><span class="payment-label">Payment Method</span><span class="payment-value">${paymentMethod}</span></div>
    <div class="payment-row"><span class="payment-label">Transaction ID</span><span class="payment-value">${txnId}</span></div>
    <div class="payment-row"><span class="payment-label">Payment Status</span><span class="payment-value">${paymentStatus}</span></div>
  </div>

  <div class="footer">
    <p>Thank you for your business!</p>
    <p style="margin-top:4px;">Rebax - Real Estate Business Accelerator</p>
  </div>
${attachmentPageHtml}
</body></html>`;

      // Show the popup first (like the app); "Open PDF" opens the print / Save as PDF dialog
      setPdfModal({
        visible: true,
        fileName: `${fileName}.pdf`,
        html: htmlContent,
        attachmentPdfUrl: attachmentUrl && attachmentType === "pdf" ? attachmentUrl : null,
      });
    } catch (error) {
      console.error("Error generating invoice PDF:", error);
      toast.error("Error opening invoice. Please try again.");
    }
  };

  // ── Expense PDF — exact same mapping as app's handleExpenseTap ──────────
  const generateExpensePDF = async (expense, e) => {
    if (e) e.stopPropagation();
    const isSubDiscount = expense.type === "subscription_discount";
    let parsedDays = 0;
    if (isSubDiscount && expense.description) {
      const match = expense.description.match(/(\d+)\s*day/i);
      if (match) parsedDays = parseInt(match[1]);
    }
    const startDate = expense.start_date || expense.expense_date || expense.created_at;
    let endDate = expense.end_date || expense.expense_date || expense.created_at;
    if (isSubDiscount && parsedDays > 0 && !expense.end_date) {
      const end = new Date(startDate);
      end.setDate(end.getDate() + parsedDays);
      endDate = end.toISOString();
    }
    const normalized = {
      id: expense.id,
      invoice_number: expense.invoice_number || `EXP-${expense.id}`,
      order_id: expense.order_id || null,
      plan_name: expense.plan_name || expense.description || expense.expense_name || "Admin Expense",
      amount: expense.amount,
      tax_amount: expense.tax_amount || (isSubDiscount ? parseFloat((parseFloat(expense.amount) * 0.18).toFixed(2)) : 0),
      total_amount: expense.total_amount || (isSubDiscount ? parseFloat((parseFloat(expense.amount) * 1.18).toFixed(2)) : parseFloat(expense.amount)),
      currency: "INR",
      transaction_id: `ADMIN-EXP-${expense.id}`,
      payment_method: "admin_expense",
      payment_status: "expense",
      start_date: startDate,
      end_date: endDate,
      issued_at: expense.created_at,
      paid_at: expense.created_at,
      created_at: expense.created_at,
      duration_days: expense.duration_days || parsedDays || 0,
      user_name: "Admin",
      user_email: "",
      metadata: {
        type: "admin_expense",
        attachment_url: expense.attachment_url || null,
        attachment_name: expense.attachment_name || null,
      },
    };
    return handleDownloadInvoicePDF(normalized, e || { stopPropagation: () => {} });
  };

  // ── Invoice tap ───────────────────────────────────────────────────────────
  const handleInvoiceTap = (item) => {
    setSelectedInvoice(item);
    setShowInvoiceModal(true);
  };

  const handleExpenseTap = (item) => {
    const isSubDiscount = item.type === "subscription_discount";

    // Parse days from description like "Extended Premium Package by 20 day(s)"
    let parsedDays = 0;
    if (isSubDiscount && item.description) {
      const match = item.description.match(/(\d+)\s*day/i);
      if (match) parsedDays = parseInt(match[1]);
    }

    // Calculate end date: start + extension days
    const startDate = item.start_date || item.expense_date || item.created_at;
    let endDate = item.end_date || item.expense_date || item.created_at;
    if (isSubDiscount && parsedDays > 0 && !item.end_date) {
      const end = new Date(startDate);
      end.setDate(end.getDate() + parsedDays);
      endDate = end.toISOString();
    }

    const invoiceData = {
      id: item.id,
      invoice_number: item.invoice_number || `EXP-${item.id}`,
      order_id: item.order_id || null,
      plan_name: item.plan_name || item.description || item.expense_name || "Admin Expense",
      amount: item.amount,
      tax_amount: item.tax_amount || (isSubDiscount ? parseFloat((parseFloat(item.amount) * 0.18).toFixed(2)) : 0),
      total_amount: item.total_amount || (isSubDiscount ? parseFloat((parseFloat(item.amount) * 1.18).toFixed(2)) : parseFloat(item.amount)),
      currency: "INR",
      transaction_id: `ADMIN-EXP-${item.id}`,
      payment_method: "admin_expense",
      payment_status: "expense",
      start_date: startDate,
      end_date: endDate,
      issued_at: item.created_at,
      paid_at: item.created_at,
      created_at: item.created_at,
      duration_days: item.duration_days || parsedDays || 0,
      user_name: "Admin",
      user_email: "",
      is_expense: true,
      metadata: {
        type: "admin_expense",
        attachment_url: item.attachment_url || item.metadata?.attachment_url || null,
        attachment_name: item.attachment_name || item.metadata?.attachment_name || null,
      },
    };
    setSelectedInvoice(invoiceData);
    setShowInvoiceModal(true);
  };

  // ── Excel export (matches app format exactly) ─────────────────────────────
  const handleExportExcel = async () => {
    if (!exportStartDate || !exportEndDate) {
      toast.warning("Please select both start and end dates");
      return;
    }
    setExporting(true);
    try {
      const res = await adminCrmService.getInvoices({ start_date: exportStartDate, end_date: exportEndDate, limit: 5000 });
      if (!res.success || !res.data) { toast.error("Failed to fetch invoices"); return; }

      const filtered = res.data;
      if (filtered.length === 0) { toast.info("No invoices found in the selected date range"); return; }

      const rows = filtered.map((inv) => {
        const pm = inv.payment_method || "";
        const isExtension = pm === "admin_extension";
        const isCreditAssignment = pm === "credit_assignment";
        const isCreditPurchase = pm === "credit_purchase";
        const isAdminExpense = (inv.is_expense || pm === "admin_expense") && !isCreditAssignment;
        const isAdminAssignment = pm === "admin_assignment";
        const isAdminAction = isAdminExpense || isExtension || isAdminAssignment || isCreditAssignment;
        const isCredit = isCreditPurchase || isCreditAssignment;
        const amount = parseFloat(inv.amount || 0);
        const tax = parseFloat(inv.tax_amount || 0);
        const total = parseFloat(inv.total_amount || 0);
        const isPaid = inv.payment_status === "paid";
        const showDates = !isCredit && !isAdminExpense;

        let type = "Subscription";
        if (isCreditAssignment) type = "Extension Credits";
        else if (isExtension) type = "Extension Subscription";
        else if (isCreditPurchase) type = "Credits";
        else if (isAdminExpense) type = "Admin Expense";

        return {
          "Date & Time": new Date(inv.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true }),
          "Invoice Number": inv.invoice_number || "",
          "Transaction ID": inv.transaction_id || "",
          "Order ID": inv.order_id || "",
          Type: type,
          "User Name": inv.user_name || "",
          "User Email": inv.user_email || "",
          "Plan Name": inv.plan_name || "",
          "Duration (Days)": inv.duration_days || "",
          Amount: amount,
          Tax: tax,
          Total: total,
          Revenue: !isAdminAction && isPaid ? total : "",
          Expense: isAdminAction ? total : "",
          Currency: inv.currency || "INR",
          "Payment Mode": isAdminAction ? "Admin" : pm,
          "Payment Status": isAdminAction ? "Free" : isPaid ? "Paid" : inv.payment_status || "",
          "Start Date": showDates && inv.start_date ? new Date(inv.start_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "",
          "End Date": showDates && inv.end_date ? new Date(inv.end_date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "",
        };
      });

      const ws = XLSX.utils.json_to_sheet(rows);
      ws["!cols"] = [
        { wch: 22 }, { wch: 28 }, { wch: 26 }, { wch: 20 }, { wch: 14 },
        { wch: 22 }, { wch: 28 }, { wch: 22 }, { wch: 12 },
        { wch: 10 }, { wch: 8 }, { wch: 10 }, { wch: 10 }, { wch: 10 },
        { wch: 6 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Invoices");
      const fileName = `Rebax_Invoices_${exportStartDate}_to_${exportEndDate}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setShowExportModal(false);
      setExportStartDate("");
      setExportEndDate("");
      toast.success(`Export Successful! ${filtered.length} invoices exported.`);
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Failed to export: " + (err.message || ""));
    } finally {
      setExporting(false);
    }
  };

  // ── Add Expense ───────────────────────────────────────────────────────────
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) { setUploadedFile(file); setExpenseForm((p) => ({ ...p, attachment: file })); }
  };

  const handleAddExpenseAPI = async () => {
    if (!expenseForm.name || !expenseForm.amount) { toast.warning("Name and amount are required"); return; }
    setAddingExpense(true);
    try {
      let attachmentUrl = null;
      let attachmentName = null;
      if (expenseForm.attachment) {
        const formData = new FormData();
        formData.append("file", expenseForm.attachment);
        const uploadRes = await adminCrmService.uploadExpenseFile(formData);
        if (uploadRes.success && uploadRes.data?.url) {
          attachmentUrl = uploadRes.data.url;
          attachmentName = expenseForm.attachment.name || uploadRes.data.originalName;
        }
      }
      const res = await adminCrmService.createExpense({
        name: expenseForm.name,
        amount: parseFloat(expenseForm.amount),
        date: expenseForm.date || new Date().toISOString().split("T")[0],
        attachment_url: attachmentUrl,
        attachment_name: attachmentName,
      });
      if (res.success) {
        toast.success("Expense added");
        setShowAddExpenseModal(false);
        setExpenseForm({ name: "", amount: "", date: "", attachment: null });
        setUploadedFile(null);
        fetchData();
      } else {
        toast.error("Failed to add expense");
      }
    } catch (err) {
      alert("Failed to add expense");
    } finally {
      setAddingExpense(false);
    }
  };

  // ── Apply filter ──────────────────────────────────────────────────────────
  const handleApplyFilter = () => {
    if (!filterStartInput || !filterEndInput) { toast.warning("Please select both start and end dates"); return; }
    const s = new Date(filterStartInput);
    const end = new Date(filterEndInput);
    if (s > end) { toast.warning("Start date must be before end date"); return; }
    setFilterStartDate(s);
    setFilterEndDate(end);
    setShowFilterModal(false);
  };

  const handleClearFilter = () => {
    setFilterStartDate(null);
    setFilterEndDate(null);
    setFilterStartInput("");
    setFilterEndInput("");
    setShowFilterModal(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="d-flex">
      <LeftNav />
      <div className="main-content flex-grow-1">
        <TopNav />
        <div className="p-4">
          <div style={{ border: "1px solid #E2E4E9", borderRadius: "10px" }} className="p-4">

            {/* Header */}
            <div className="billing-summary-header">
              <div className="d-flex align-items-center gap-2">
                <button className="billing-back-btn" onClick={() => navigate(-1)}>
                  <img src={arrowleftt} alt="back" />
                </button>
                <h1 className="billing-title">Billing Summary</h1>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button className="billing-icon-btn" onClick={() => setShowExportModal(true)} disabled={exporting}>
                  <img src={downloadIcon} alt="download" />
                </button>
                <button
                  className="billing-icon-btn"
                  onClick={() => setShowFilterModal(true)}
                  style={{ borderColor: filterStartDate && filterEndDate ? "#6C5CE7" : undefined }}
                >
                  <img src={filterIcon} alt="filter" />
                </button>
              </div>
            </div>

            {/* Period tabs */}
            <div className="billing-tabs1">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  className={`billing-tab ${selectedPeriod === p.key ? "active" : ""}`}
                  onClick={() => { setFilterStartDate(null); setFilterEndDate(null); setSelectedPeriod(p.key); }}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Active filter indicator */}
            {filterStartDate && filterEndDate && (
              <div style={{ background: "#F0EDFF", borderRadius: 8, padding: "10px 14px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 13, color: "#555" }}>
                  📅 Filtered: {filterStartDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} — {filterEndDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                </span>
                <button style={{ background: "none", border: "none", color: "#6C5CE7", fontSize: 18, cursor: "pointer", padding: 0 }} onClick={handleClearFilter}>×</button>
              </div>
            )}

            {/* Stats Cards */}
            <div className="billing-stats-row">
              <div className="billing-stat-card">
                <div className="stat-label">Total Invoices</div>
                <div className="stat-value">{stats?.total_invoices || 0}</div>
                {renderStatChange(stats?.invoice_change)}
              </div>
              <div className="billing-stat-card">
                <div className="stat-label">Total Revenue</div>
                <div className="stat-value">{fmtNum(stats?.total_revenue)}</div>
                {renderStatChange(stats?.revenue_change)}
              </div>
              <div className="billing-stat-card">
                <div className="stat-label">Total Expenses</div>
                <div className="stat-value">{fmtNum(stats?.total_expenses)}</div>
                {renderStatChange(stats?.expense_change)}
              </div>
            </div>
            <div className="billing-stats-row">
              <div className="billing-stat-card">
                <div className="stat-label">Total Discounts</div>
                <div className="stat-value">{fmtNum(stats?.total_discounts)}</div>
                {renderStatChange(stats?.discount_change)}
              </div>
              <div className="billing-stat-card">
                <div className="stat-label">Total Gross</div>
                <div className="stat-value">{fmtNum(stats?.total_gross)}</div>
                {renderStatChange(stats?.gross_change)}
              </div>
            </div>

            {/* Tabs: Invoices / Expenses */}
            <div className="billing-tabs">
              <div className="tab-row">
                <button className={`tab-button ${activeTab === "invoices" ? "active" : ""}`} onClick={() => setActiveTab("invoices")}>
                  Invoices ({filteredInvoices.length})
                </button>
                <button className={`tab-button ${activeTab === "expenses" ? "active" : ""}`} onClick={() => setActiveTab("expenses")}>
                  Expenses ({filteredExpenses.length})
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <div style={{ display: "flex", alignItems: "center", background: "#F3F4F6", borderRadius: 10, padding: "0 12px", height: 42, margin: "12px 0" }}>
              <svg width="18" height="18" fill="none" stroke="#9CA3AF" strokeWidth="2" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                style={{ flex: 1, border: "none", background: "transparent", outline: "none", marginLeft: 8, fontSize: 14, color: "#1F2937" }}
                placeholder="Search by Order ID, Invoice No, Name, Email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <button style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 18, padding: 0 }} onClick={() => setSearchQuery("")}>×</button>
              )}
            </div>

            {/* Tab Content */}
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
              </div>
            ) : activeTab === "invoices" ? (
              <div className="billing-invoice-grid">
                {filteredInvoices.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}>
                    <p>{q ? "No invoices match your search" : "No invoices for this period"}</p>
                  </div>
                ) : (
                  filteredInvoices.map((invoice) => {
                    const isExp = invoice.is_expense || invoice.payment_method === "admin_expense";
                    const status = invoice.payment_status || "pending";
                    const isPaid = status === "paid";
                    const isFree = status === "free";
                    let statusText, statusClass;
                    if (isExp) { statusText = "Expense"; statusClass = "expense"; }
                    else if (isPaid) { statusText = "Paid"; statusClass = "paid"; }
                    else if (isFree) { statusText = "Free"; statusClass = "free"; }
                    else { statusText = status.charAt(0).toUpperCase() + status.slice(1); statusClass = "pending"; }

                    return (
                      <div key={invoice.id} className="billing-item invoice-card" onClick={() => handleInvoiceTap(invoice)} style={{ cursor: "pointer" }}>
                        <div className="invoice-left">
                          <div className="invoice-header">
                            <span className="invoice-id">{invoice.invoice_number || "N/A"}</span>
                            <span className={`billing-status ${statusClass}`}>{statusText}</span>
                          </div>
                          {invoice.order_id && (
                            <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 4 }}>
                              Order ID: <span style={{ color: "#374151", fontWeight: 500 }}>{invoice.order_id}</span>
                            </div>
                          )}
                          <div className="invoice-name-date">
                            <span className="billing-name">{invoice.user_name || invoice.plan_name || "N/A"}</span>
                            <span className="invoice-date-small">
                              {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                            </span>
                          </div>
                          <div className="billing-price">
                            {isExp ? `-₹${fmtNum(invoice.total_amount)}` : `₹${fmtNum(invoice.total_amount)}`}
                          </div>
                        </div>
                        <div className="invoice-right">
                          <div className="invoice-icon-wrapper clickable" onClick={(e) => handleDownloadInvoicePDF(invoice, e)} title="Download Invoice PDF">
                            <img src={invoicewebadmin} alt="invoice" className="invoice-icon" />
                            <span className="invoice-label">Invoice PDF</span>
                          </div>
                          {invoice.duration_days > 0 && (
                            <div className="invoice-icon-wrapper">
                              <img src={attachementwebadmin} alt="attachment" className="attachment-icon" />
                              <span className="attachment-label">Attachment</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            ) : (
              <div className="billing-list">
                {filteredExpenses.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 40, color: "#9CA3AF" }}>
                    <p>{q ? "No expenses match your search" : "No expenses for this period"}</p>
                  </div>
                ) : (
                  filteredExpenses.map((expense) => (
                    <div key={expense.id} className="billing-item expense-card clickable" onClick={() => handleExpenseTap(expense)} style={{ cursor: "pointer" }}>
                      <div className="expense-left">
                        <div className="expense-title">{expense.description || expense.expense_name || "Expense"}</div>
                        <div className="billing-name">Admin</div>
                        <div className="expense-invoice" onClick={(e) => generateExpensePDF(expense, e)} style={{ cursor: "pointer" }} title="Download Expense Invoice PDF">
                          <img src={invoicewebadmin} alt="invoice" className="expense-invoice-icon" />
                          <span className="expense-invoice-text">Invoice PDF</span>
                        </div>
                      </div>
                      <div className="expense-right">
                        <span className="billing-status expense-status discounted">Expense</span>
                        <div className="expense-amount" style={{ color: "#DC2626" }}>-₹{fmtNum(expense.amount)}</div>
                        <div className="expense-date">
                          {expense.expense_date ? new Date(expense.expense_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FAB: Add Expense */}
      <button className="add-expense-btn-fixed" onClick={() => setShowAddExpenseModal(true)}>
        Add Expense
      </button>

      {/* ── Filter Modal ── */}
      {showFilterModal && (
        <div className="expense-modal-overlay" onClick={() => setShowFilterModal(false)}>
          <div className="expense-modal" onClick={(e) => e.stopPropagation()}>
            <div className="expense-modal-header">
              <h3 className="expense-modal-title">Filter by Date</h3>
              <button className="expense-modal-close" onClick={() => setShowFilterModal(false)}>
                <img src={close} alt="close" />
              </button>
            </div>
            <div className="expense-modal-body">
              <div className="expense-form-group">
                <label className="expense-label">Start Date</label>
                <input type="date" className="expense-input" value={filterStartInput} onChange={(e) => setFilterStartInput(e.target.value)} />
              </div>
              <div className="expense-form-group">
                <label className="expense-label">End Date</label>
                <input type="date" className="expense-input" value={filterEndInput} onChange={(e) => setFilterEndInput(e.target.value)} />
              </div>
            </div>
            <div className="expense-modal-footer">
              <button className="expense-btn-cancel" onClick={handleClearFilter}>Clear</button>
              <button className="expense-btn-add" onClick={handleApplyFilter}>Apply</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Add Expense Modal ── */}
      {showAddExpenseModal && (
        <div className="expense-modal-overlay" onClick={() => setShowAddExpenseModal(false)}>
          <div className="expense-modal" onClick={(e) => e.stopPropagation()}>
            <div className="expense-modal-header">
              <h3 className="expense-modal-title">Add Expense</h3>
              <button className="expense-modal-close" onClick={() => setShowAddExpenseModal(false)}>
                <img src={close} alt="close" />
              </button>
            </div>
            <div className="expense-modal-body">
              <div className="expense-form-group">
                <label className="expense-label">Expense Name</label>
                <input type="text" className="expense-input" placeholder="Enter Expense Name" value={expenseForm.name} onChange={(e) => setExpenseForm((p) => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="expense-form-group">
                <label className="expense-label">Select Date</label>
                <input type="date" className="expense-input" value={expenseForm.date} onChange={(e) => setExpenseForm((p) => ({ ...p, date: e.target.value }))} />
              </div>
              <div className="expense-form-group">
                <label className="expense-label">Amount</label>
                <input type="number" className="expense-input" placeholder="Enter Amount" value={expenseForm.amount} onChange={(e) => setExpenseForm((p) => ({ ...p, amount: e.target.value }))} />
              </div>
              <div className="expense-form-group">
                <label className="expense-label">Attach Bill</label>
                <div className="expense-upload-area">
                  {!uploadedFile ? (
                    <>
                      <div className="expense-upload-box">
                        <div className="upload-icon">📁</div>
                        <div className="upload-text">
                          Drag and Drop here or{" "}
                          <label className="upload-link">
                            Choose File
                            <input type="file" accept=".pdf,image/*" hidden onChange={handleFileUpload} />
                          </label>
                        </div>
                      </div>
                      <div className="upload-format-text">Supported formats: PDF, Images</div>
                      <div className="upload-size">Max size: 25MB</div>
                    </>
                  ) : (
                    <div className="uploaded-file">
                      <div className="file-info">
                        <span className="file-icon">📄</span>
                        <div className="file-details">
                          <div className="file-name">{uploadedFile.name}</div>
                          <div className="file-size">{(uploadedFile.size / 1024).toFixed(2)} KB</div>
                        </div>
                      </div>
                      <button className="file-remove" onClick={() => { setUploadedFile(null); setExpenseForm((p) => ({ ...p, attachment: null })); }}>🗑️</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="expense-modal-footer">
              <button className="expense-btn-cancel" onClick={() => { setShowAddExpenseModal(false); setExpenseForm({ name: "", amount: "", date: "", attachment: null }); setUploadedFile(null); }}>Cancel</button>
              <button className="expense-btn-add" onClick={handleAddExpenseAPI} disabled={addingExpense}>
                {addingExpense ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Invoice Detail Modal ── */}
      {showInvoiceModal && selectedInvoice && (
        <div className="expense-modal-overlay" onClick={() => { setShowInvoiceModal(false); setSelectedInvoice(null); }}>
          <div className="expense-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="expense-modal-header">
              <h2 className="expense-modal-title">
                {selectedInvoice.is_expense ? "Expense Details" : "Invoice Details"}
              </h2>
              <button className="expense-modal-close" onClick={() => { setShowInvoiceModal(false); setSelectedInvoice(null); }}>
                <img src={close} alt="close" />
              </button>
            </div>
            <div className="expense-modal-body">
              <div className="invoice-detail-section">
                {[
                  ["Invoice #", selectedInvoice.invoice_number],
                  ["Order ID", selectedInvoice.order_id],
                  ["Transaction ID", selectedInvoice.transaction_id],
                  ["User Name", selectedInvoice.user_name],
                  ["User Email", selectedInvoice.user_email],
                  ["Plan", selectedInvoice.plan_name],
                  ["Amount", selectedInvoice.amount != null ? `₹${fmtNum(selectedInvoice.amount)}` : null],
                  ["Tax", selectedInvoice.tax_amount != null ? `₹${fmtNum(selectedInvoice.tax_amount)}` : null],
                  ["Total", selectedInvoice.total_amount != null ? `₹${fmtNum(selectedInvoice.total_amount)}` : null],
                  ["Payment Method", selectedInvoice.payment_method],
                  ["Payment Status", selectedInvoice.payment_status],
                  ["Duration", selectedInvoice.duration_days ? selectedInvoice.duration_days + " days" : null],
                  ["Start Date", selectedInvoice.start_date ? formatDate(selectedInvoice.start_date) : null],
                  ["End Date", selectedInvoice.end_date ? formatDate(selectedInvoice.end_date) : null],
                  ["Date", selectedInvoice.created_at ? formatDate(selectedInvoice.created_at) : null],
                ]
                  .filter(([, v]) => v)
                  .map(([label, value]) => (
                    <div key={label} className="invoice-detail-row">
                      <label className="invoice-detail-label">{label}:</label>
                      <span className="invoice-detail-value">{value || "N/A"}</span>
                    </div>
                  ))}
              </div>
            </div>
            <div className="expense-modal-footer">
              <button
                className="expense-btn-add"
                onClick={(e) =>
                  selectedInvoice.is_expense
                    ? generateExpensePDF(selectedInvoice, e)
                    : handleDownloadInvoicePDF(selectedInvoice, e)
                }
              >
                Download PDF
              </button>
              <button className="expense-btn-cancel" onClick={() => { setShowInvoiceModal(false); setSelectedInvoice(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Export Excel Modal ── */}
      {showExportModal && (
        <div className="expense-modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="expense-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="expense-modal-header">
              <h2 className="expense-modal-title">Export Invoices</h2>
              <button className="expense-modal-close" onClick={() => setShowExportModal(false)}>
                <img src={close} alt="close" />
              </button>
            </div>
            <div className="expense-modal-body">
              <div className="expense-form-group">
                <label className="expense-label">Start Date</label>
                <input type="date" className="expense-input" value={exportStartDate} onChange={(e) => setExportStartDate(e.target.value)} />
              </div>
              <div className="expense-form-group">
                <label className="expense-label">End Date</label>
                <input type="date" className="expense-input" value={exportEndDate} onChange={(e) => setExportEndDate(e.target.value)} />
              </div>
            </div>
            <div className="expense-modal-footer">
              <button className="expense-btn-cancel" onClick={() => setShowExportModal(false)}>Cancel</button>
              <button className="expense-btn-add" onClick={handleExportExcel} disabled={!exportStartDate || !exportEndDate || exporting}>
                {exporting ? "Exporting..." : "Download Excel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Post-download popup (matches app's pdfModal) */}
      {pdfModal.visible && (
        <div
          style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 }}
          onClick={() => setPdfModal({ visible: false, fileName: "", html: "", attachmentPdfUrl: null })}
        >
          <div
            style={{ backgroundColor: "#fff", borderRadius: 20, padding: 30, display: "flex", flexDirection: "column", alignItems: "center", width: "90%", maxWidth: 400 }}
            onClick={(e) => e.stopPropagation()}
          >
            <IoCheckmarkCircle size={60} color="#22c55e" />
            <div style={{ fontSize: 22, fontWeight: 700, color: "#1a1a2e", marginTop: 15, marginBottom: 10 }}>PDF Ready!</div>
            <div style={{ fontSize: 14, color: "#888", marginBottom: 5 }}>File name:</div>
            <div style={{ fontSize: 12, color: "#7C3AED", fontWeight: 500, marginBottom: 20, textAlign: "center", wordBreak: "break-all" }}>{pdfModal.fileName}</div>
            <button
              type="button"
              style={{ backgroundColor: "#7C3AED", color: "#fff", fontWeight: 600, fontSize: 16, padding: "12px 40px", borderRadius: 10, border: "none", width: "100%", marginBottom: 8, cursor: "pointer" }}
              onClick={() => printInvoiceHtml(pdfModal.html)}
            >
              Open PDF
            </button>
            {pdfModal.attachmentPdfUrl && (
              <button
                type="button"
                style={{ backgroundColor: "#059669", color: "#fff", fontWeight: 600, fontSize: 16, padding: "12px 40px", borderRadius: 10, border: "none", width: "100%", marginBottom: 8, cursor: "pointer" }}
                onClick={() => window.open(pdfModal.attachmentPdfUrl, "_blank", "noopener")}
              >
                Open Attached Bill
              </button>
            )}
            <button
              type="button"
              style={{ background: "none", color: "#888", fontWeight: 500, fontSize: 15, padding: "10px 40px", borderRadius: 10, border: "none", width: "100%", cursor: "pointer" }}
              onClick={() => setPdfModal({ visible: false, fileName: "", html: "", attachmentPdfUrl: null })}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
